import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { fetchActivitySession } from '../../api/activitySessions';
import { ActivitySession } from '../ActivitySession/ActivitySession';
import { useTranslation } from '../../../../shared/i18n';

/**
 * SessionPage - Page component that fetches and displays an activity session
 * Uses URL param :sessionId to fetch the session data from the API
 */
export const SessionPage = ({ data }) => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sessionData, setSessionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSession = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await fetchActivitySession(sessionId);
        setSessionData(result);
      } catch (err) {
        console.error('Error fetching session:', err);
        setError(err.message || 'Failed to load session');
      } finally {
        setIsLoading(false);
      }
    };

    if (sessionId) {
      loadSession();
    }
  }, [sessionId]);

  const handleComplete = () => {
    // Navigate back to dashboard after completion
    navigate('/');
    // Also reload to refresh dashboard state
    window.location.reload();
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">Error</h4>
        <p>{error}</p>
        <button className="btn btn-outline-danger" onClick={() => navigate('/')}>
          Return to Dashboard
        </button>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="alert alert-warning" role="alert">
        <p className="mb-0">Session not found</p>
      </div>
    );
  }

  const { session, activity, pending_skip_request, next_activities } = sessionData;

  return (
    <section data-child-id={data?.child?.id}>
      <ActivitySession
        session={session}
        activity={activity}
        nextActivities={next_activities}
        onComplete={handleComplete}
        onCancel={handleCancel}
        initialSkipRequestPending={!!pending_skip_request}
      />
    </section>
  );
};
